import React, { useState, useEffect } from 'react';
import { Bell, Target, Clock, AlertCircle, Lightbulb, Heart, TrendingUp, Users, Globe, Zap, Award, BookOpen } from 'lucide-react';

interface GuidanceRule {
  id: string;
  name: string;
  description: string;
  condition: (userProfile: any, userBehavior: any) => boolean;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  category: 'learning' | 'financial' | 'career' | 'health' | 'community' | 'legal' | 'housing';
  guidance: {
    title: string;
    message: string;
    actionText: string;
    actionUrl?: string;
    progressContext?: string;
    emotionalTone: 'supportive' | 'encouraging' | 'urgent' | 'celebratory' | 'informative';
  };
  cooldown: number; // hours
  maxFrequency: number; // per week
}

interface ProactiveGuidance {
  id: string;
  ruleId: string;
  title: string;
  message: string;
  actionText: string;
  actionUrl?: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  category: string;
  progressContext?: string;
  emotionalTone: 'supportive' | 'encouraging' | 'urgent' | 'celebratory' | 'informative';
  createdAt: Date;
  expiresAt: Date;
  isRead: boolean;
  isDismissed: boolean;
  progressRelevance: number;
  personalizationScore: number;
}

interface ProactiveGuidanceSystemProps {
  userProfile: {
    id: string;
    currentLocation: string;
    interests: string[];
    challenges: string[];
    goals: string[];
    learningStyle: string;
    currentMood: string;
    experienceLevel: string;
    completedLessons: string[];
    currentStreak: number;
    totalPoints: number;
    lastActive: Date;
  };
  userBehavior: {
    completedItems: string[];
    timeSpent: Record<string, number>;
    ratings: Record<string, number>;
    searchHistory: string[];
    clickHistory: string[];
    bounceRate: Record<string, number>;
    completionRate: Record<string, number>;
    preferredCategories: string[];
    preferredDifficulty: string;
    preferredTimeSlots: string[];
    progressEngagement: Record<string, number>;
    lastGuidanceInteraction: Date;
    guidanceHistory: string[];
  };
  onGuidanceAction?: (guidance: ProactiveGuidance) => void;
  onDismissGuidance?: (guidanceId: string) => void;
}

const ProactiveGuidanceSystem: React.FC<ProactiveGuidanceSystemProps> = ({
  userProfile,
  userBehavior,
  onGuidanceAction,
  onDismissGuidance
}) => {
  const [guidance, setGuidance] = useState<ProactiveGuidance[]>([]);
  const [filteredGuidance, setFilteredGuidance] = useState<ProactiveGuidance[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showDismissed, setShowDismissed] = useState(false);

  // Define guidance rules
  const guidanceRules: GuidanceRule[] = [
    // Learning rules
    {
      id: 'learning-streak-reminder',
      name: 'Learning Streak Reminder',
      description: 'Remind user to maintain learning streak',
      condition: (userProfile) => {
        const daysSinceLastActive = Math.floor((Date.now() - userProfile.lastActive.getTime()) / (1000 * 60 * 60 * 24));
        return userProfile.currentStreak > 0 && daysSinceLastActive >= 1;
      },
      priority: 'medium',
      category: 'learning',
      guidance: {
        title: 'Keep Your Learning Streak Alive!',
        message: `You're on a ${userProfile.currentStreak}-day learning streak! Don't let it break - even 10 minutes of learning today will keep your momentum going.`,
        actionText: 'Continue Learning',
        actionUrl: '/journey-hub',
        progressContext: 'Consistent learning builds momentum and helps you achieve your goals faster. Your dedication will pay off.',
        emotionalTone: 'encouraging'
      },
      cooldown: 24,
      maxFrequency: 3
    },
    {
      id: 'learning-goal-progress',
      name: 'Learning Goal Progress',
      description: 'Celebrate learning milestones and suggest next steps',
      condition: (userProfile) => {
        return userProfile.completedLessons.length > 0 && userProfile.completedLessons.length % 5 === 0;
      },
      priority: 'high',
      category: 'learning',
      guidance: {
        title: 'Amazing Progress! 🎉',
        message: `Congratulations! You've completed ${userProfile.completedLessons.length} lessons. Your dedication to learning is inspiring. What would you like to explore next?`,
        actionText: 'Explore More',
        actionUrl: '/journey-hub',
        progressContext: 'Education is the foundation of success. Your progress shows dedication and will help you achieve your goals.',
        emotionalTone: 'celebratory'
      },
      cooldown: 168, // 1 week
      maxFrequency: 1
    },
    // Financial rules
    {
      id: 'credit-building-reminder',
      name: 'Credit Building Reminder',
      description: 'Remind user about credit building importance',
      condition: (userProfile, behavior) => {
        return userProfile.challenges.includes('credit building') && 
               !behavior.completedItems.includes('credit-course');
      },
      priority: 'high',
      category: 'financial',
      guidance: {
        title: 'Build Your Credit Foundation',
        message: 'Credit is crucial for your financial future in America. Start building your credit score now to unlock opportunities like home ownership and better loan rates.',
        actionText: 'Learn About Credit',
        actionUrl: '/toolkit/credit-guide',
        progressContext: 'Credit systems are important for financial success. Understanding them early will help you achieve your financial goals.',
        emotionalTone: 'informative'
      },
      cooldown: 72, // 3 days
      maxFrequency: 2
    },
    {
      id: 'tax-season-reminder',
      name: 'Tax Season Reminder',
      description: 'Remind user about tax filing',
      condition: () => {
        const currentMonth = new Date().getMonth();
        return currentMonth >= 0 && currentMonth <= 3; // January to April
      },
      priority: 'high',
      category: 'financial',
      guidance: {
        title: 'Tax Season is Here!',
        message: 'It\'s tax season in America. Make sure you understand your tax obligations and file on time to avoid penalties.',
        actionText: 'Learn About Taxes',
        actionUrl: '/toolkit/tax-guide',
        progressContext: 'Tax systems can be complex. Understanding how they work is essential for your financial success.',
        emotionalTone: 'urgent'
      },
      cooldown: 168, // 1 week
      maxFrequency: 2
    },
    // Career rules
    {
      id: 'job-search-encouragement',
      name: 'Job Search Encouragement',
      description: 'Encourage job search activities',
      condition: (userProfile) => {
        return userProfile.challenges.includes('job search') && 
               userProfile.goals.some((goal: string) => goal.includes('career') || goal.includes('job'));
      },
      priority: 'medium',
      category: 'career',
      guidance: {
        title: 'Your Dream Job Awaits!',
        message: 'Ready to advance your career? I can help you find job opportunities, improve your resume, and prepare for interviews.',
        actionText: 'Find Jobs',
        actionUrl: '/toolkit/job-search',
        progressContext: 'Career success often requires networking and understanding professional norms in job applications.',
        emotionalTone: 'encouraging'
      },
      cooldown: 48, // 2 days
      maxFrequency: 2
    },
    // Health rules
    {
      id: 'healthcare-navigation',
      name: 'Healthcare Navigation',
      description: 'Help with healthcare system navigation',
      condition: (_, behavior) => {
        return !behavior.completedItems.includes('healthcare-guide');
      },
      priority: 'medium',
      category: 'health',
      guidance: {
        title: 'Navigate Healthcare with Confidence',
        message: 'Understanding the US healthcare system can be overwhelming. Let me help you find doctors, understand insurance, and access the care you need.',
        actionText: 'Learn About Healthcare',
        actionUrl: '/toolkit/healthcare-guide',
        progressContext: 'Healthcare systems can be complex. Understanding how they work will help you stay healthy and avoid unexpected costs.',
        emotionalTone: 'informative'
      },
      cooldown: 168, // 1 week
      maxFrequency: 1
    },
    // Housing rules
    {
      id: 'housing-opportunities',
      name: 'Housing Opportunities',
      description: 'Suggest housing-related resources',
      condition: (userProfile) => {
        return userProfile.goals.some((goal: string) => goal.includes('home') || goal.includes('housing'));
      },
      priority: 'medium',
      category: 'housing',
      guidance: {
        title: 'Your Home Ownership Journey',
        message: 'Ready to find your perfect home? I can help you understand the housing market, mortgage options, and the home buying process.',
        actionText: 'Explore Housing',
        actionUrl: '/toolkit/housing-guide',
        progressContext: 'Home ownership is a significant financial milestone. Understanding the process will help you achieve this important goal.',
        emotionalTone: 'encouraging'
      },
      cooldown: 168, // 1 week
      maxFrequency: 1
    }
  ];

  useEffect(() => {
    generateGuidance();
  }, [userProfile, userBehavior]);

  useEffect(() => {
    filterGuidance();
  }, [selectedCategory, showDismissed, guidance]);

  const generateGuidance = () => {
    const newGuidance: ProactiveGuidance[] = [];

    guidanceRules.forEach(rule => {
      if (rule.condition(userProfile, userBehavior)) {
        // Check cooldown and frequency limits
        const lastGuidance = guidance.find(g => g.ruleId === rule.id);
        const now = new Date();
        
        if (lastGuidance) {
          const hoursSinceLastGuidance = (now.getTime() - lastGuidance.createdAt.getTime()) / (1000 * 60 * 60);
          if (hoursSinceLastGuidance < rule.cooldown) return;
        }

        // Check frequency limit
        const recentGuidance = guidance.filter(g => 
          g.ruleId === rule.id && 
          (now.getTime() - g.createdAt.getTime()) < (7 * 24 * 60 * 60 * 1000) // Last 7 days
        );
        if (recentGuidance.length >= rule.maxFrequency) return;

        // Generate guidance
        const guidanceItem: ProactiveGuidance = {
          id: `${rule.id}-${Date.now()}`,
          ruleId: rule.id,
          title: rule.guidance.title,
          message: rule.guidance.message,
          actionText: rule.guidance.actionText,
          actionUrl: rule.guidance.actionUrl,
          priority: rule.priority,
          category: rule.category,
          progressContext: rule.guidance.progressContext,
          emotionalTone: rule.guidance.emotionalTone,
          createdAt: now,
          expiresAt: new Date(now.getTime() + (7 * 24 * 60 * 60 * 1000)), // Expires in 7 days
          isRead: false,
          isDismissed: false,
          progressRelevance: calculateProgressRelevance(rule),
          personalizationScore: calculatePersonalizationScore(rule)
        };

        newGuidance.push(guidanceItem);
      }
    });

    setGuidance(prev => [...newGuidance, ...prev.filter(g => !g.isDismissed && g.expiresAt > new Date())]);
  };

  const calculateProgressRelevance = (rule: GuidanceRule): number => {
    let score = 50; // Base score

    if (rule.guidance.progressContext) {
      score += 30; // Progress context adds relevance
    }

    if (rule.category === 'learning') {
      score += 20; // Learning category gets bonus
    }

    return Math.min(score, 100);
  };

  const calculatePersonalizationScore = (rule: GuidanceRule): number => {
    let score = 0;

    // Interest matching
    const interestMatch = rule.guidance.message.toLowerCase().includes(userProfile.interests.join(' ').toLowerCase());
    if (interestMatch) score += 25;

    // Challenge matching
    const challengeMatch = rule.guidance.message.toLowerCase().includes(userProfile.challenges.join(' ').toLowerCase());
    if (challengeMatch) score += 25;

    // Goal matching
    const goalMatch = rule.guidance.message.toLowerCase().includes(userProfile.goals.join(' ').toLowerCase());
    if (goalMatch) score += 25;

    // Progress matching
    if (rule.guidance.progressContext && userProfile.completedLessons.length > 0) {
      score += 25;
    }

    return Math.min(score, 100);
  };

  const filterGuidance = () => {
    let filtered = guidance;

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(g => g.category === selectedCategory);
    }

    if (!showDismissed) {
      filtered = filtered.filter(g => !g.isDismissed);
    }

    // Sort by priority and personalization score
    filtered.sort((a, b) => {
      const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };
      if (priorityOrder[b.priority] !== priorityOrder[a.priority]) {
        return priorityOrder[b.priority] - priorityOrder[a.priority];
      }
      return b.personalizationScore - a.personalizationScore;
    });

    setFilteredGuidance(filtered);
  };

  const handleGuidanceAction = (guidanceItem: ProactiveGuidance) => {
    setGuidance(prev => prev.map(g => 
      g.id === guidanceItem.id ? { ...g, isRead: true } : g
    ));
    onGuidanceAction?.(guidanceItem);
  };

  const handleDismissGuidance = (guidanceId: string) => {
    setGuidance(prev => prev.map(g => 
      g.id === guidanceId ? { ...g, isDismissed: true } : g
    ));
    onDismissGuidance?.(guidanceId);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'text-red-600 bg-red-100 border-red-200';
      case 'high':
        return 'text-orange-600 bg-orange-100 border-orange-200';
      case 'medium':
        return 'text-yellow-600 bg-yellow-100 border-yellow-200';
      case 'low':
        return 'text-blue-600 bg-blue-100 border-blue-200';
      default:
        return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'learning':
        return <BookOpen className="w-5 h-5" />;
      case 'financial':
        return <Target className="w-5 h-5" />;
      case 'career':
        return <TrendingUp className="w-5 h-5" />;
      case 'health':
        return <Heart className="w-5 h-5" />;
      case 'community':
        return <Users className="w-5 h-5" />;
      case 'legal':
        return <Award className="w-5 h-5" />;
      case 'housing':
        return <Globe className="w-5 h-5" />;
      default:
        return <Lightbulb className="w-5 h-5" />;
    }
  };

  const getEmotionalToneIcon = (tone: string) => {
    switch (tone) {
      case 'supportive':
        return <Heart className="w-4 h-4 text-pink-500" />;
      case 'encouraging':
        return <Zap className="w-4 h-4 text-yellow-500" />;
      case 'urgent':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'celebratory':
        return <Award className="w-4 h-4 text-green-500" />;
      case 'informative':
        return <Lightbulb className="w-4 h-4 text-blue-500" />;
      default:
        return <Bell className="w-4 h-4 text-gray-500" />;
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Proactive Guidance</h2>
        <p className="text-gray-600">
          Personalized recommendations and timely reminders to help you succeed
        </p>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap gap-4 justify-center">
        <div className="flex gap-2">
          {['all', 'learning', 'financial', 'career', 'health', 'housing'].map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === category
                  ? 'bg-primary-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </button>
          ))}
        </div>
        
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={showDismissed}
            onChange={(e) => setShowDismissed(e.target.checked)}
            className="rounded border-gray-300"
          />
          Show dismissed
        </label>
      </div>

      {/* Guidance List */}
      <div className="space-y-4">
        {filteredGuidance.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-gray-200">
            <Bell className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">
              No guidance available
            </h3>
            <p className="text-gray-600">
              Complete your profile and start using the app to receive personalized guidance.
            </p>
          </div>
        ) : (
          filteredGuidance.map((guidanceItem) => (
            <div
              key={guidanceItem.id}
              className={`bg-white rounded-2xl border-2 p-6 transition-all duration-200 ${
                guidanceItem.isDismissed ? 'opacity-50' : 'hover:shadow-lg'
              } ${getPriorityColor(guidanceItem.priority)}`}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    {getCategoryIcon(guidanceItem.category)}
                    {getEmotionalToneIcon(guidanceItem.emotionalTone)}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">{guidanceItem.title}</h3>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <span className="capitalize">{guidanceItem.category}</span>
                      <span>•</span>
                      <span className="capitalize">{guidanceItem.priority} priority</span>
                      <span>•</span>
                      <span>{guidanceItem.personalizationScore}% personalized</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {!guidanceItem.isRead && (
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  )}
                  <button
                    onClick={() => handleDismissGuidance(guidanceItem.id)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    ✕
                  </button>
                </div>
              </div>

              {/* Message */}
              <p className="text-gray-700 mb-4 leading-relaxed">
                {guidanceItem.message}
              </p>

              {/* Progress Context */}
              {guidanceItem.progressContext && (
                <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800">
                    <strong>Progress Insight:</strong> {guidanceItem.progressContext}
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    <span>{guidanceItem.createdAt.toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Globe className="w-4 h-4" />
                    <span>{guidanceItem.progressRelevance}% progress relevance</span>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <button
                    onClick={() => handleGuidanceAction(guidanceItem)}
                    className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                  >
                    {guidanceItem.actionText}
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ProactiveGuidanceSystem;
